import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';

export interface Tab {
  id: string;
  title: string;
  component: string;
  /** If provided, the tab is only shown when the user has at least one of these permissions.
   *  An empty/undefined array means the tab is always visible. */
  permissions?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class BrokerTabsService {
  private activeTabSubject = new BehaviorSubject<string>('overview');
  public activeTab$ = this.activeTabSubject.asObservable();

  /** Full tab catalog with their required permissions */
  private allTabs: Tab[] = [
    // Always visible — every broker sees the overview
    { id: 'overview', title: 'brokerDashboard.tabs.overview', component: 'OverviewComponent' },

    {
      id: 'crm-dashboard',
      title: 'brokerDashboard.tabs.crmDashboard',
      component: 'CrmDashboardComponent',
      permissions: [
        'lead:read_own',
        'lead:read_all',
        'booking:manage',
        'booking:read_all',
        'inquiry:read_own',
        'inquiry:read_all',
      ],
    },

    // Calendar / Viewings
    {
      id: 'calendar',
      title: 'brokerDashboard.tabs.calendar',
      component: 'CalendarComponent',
      permissions: ['booking:view_calendar', 'viewing:manage'],
    },

    // Marketing / Campaigns
    {
      id: 'marketing',
      title: 'brokerDashboard.tabs.marketing',
      component: 'MarketingComponent',
      permissions: ['campaign:create', 'campaign:view_analytics', 'campaign:manage_budget'],
    },

    // Commissions
    {
      id: 'commissions',
      title: 'brokerDashboard.tabs.commissions',
      component: 'CommissionsComponent',
      permissions: ['commission:read_own', 'commission:read_all'],
    },

    // Projects management
    {
      id: 'projects',
      title: 'brokerDashboard.tabs.projects',
      component: 'Projects',
      permissions: ['unit:read', 'unit:create', 'unit:update'],
    },

    // Units management
    {
      id: 'units',
      title: 'brokerDashboard.tabs.units',
      component: 'UnitsComponent',
      permissions: ['unit:read', 'unit:create', 'unit:update', 'unit:delete'],
    },

    // Offers
    {
      id: 'offers',
      title: 'brokerDashboard.tabs.offers',
      component: 'OffersComponent',
      permissions: ['unit:read'],
    },

    // Staff management
    {
      id: 'staff',
      title: 'brokerDashboard.tabs.staff',
      component: 'StaffComponent',
      permissions: ['staff:manage'],
    },

    // Always visible — settings & profile
    { id: 'settings', title: 'brokerDashboard.tabs.settings', component: 'SettingsComponent' },
    { id: 'profile', title: 'brokerDashboard.tabs.profile', component: 'ProfileComponent' },
  ];

  private readonly authService = inject(AuthService);
  /**
   * Returns only the tabs the current user is allowed to see.
   * A tab with no `permissions` array is always included (e.g. overview, profile).
   * A tab with a `permissions` array is included if the user has at least one of those permissions.
   */
  getTabs(): Tab[] {
    const role = this.authService.getRole();
    if (role === 'broker_company') {
      return this.allTabs;
    }

    return this.allTabs.filter((tab) => {
      if (!tab.permissions || tab.permissions.length === 0) return true;
      return this.authService.hasAnyPermission(tab.permissions);
    });
  }

  getActiveTab(): string {
    return this.activeTabSubject.value;
  }

  setActiveTab(tabId: string): void {
    this.activeTabSubject.next(tabId);
  }

  getTabById(tabId: string): Tab | undefined {
    return this.allTabs.find((tab) => tab.id === tabId);
  }

  /** Returns true if the user has the given permission */
  can(permission: string): boolean {
    if (this.authService.getRole() === 'broker_company') return true;
    return this.authService.hasPermission(permission);
  }

  /** Returns true if the user has at least one of the given permissions */
  canAny(permissions: string[]): boolean {
    if (this.authService.getRole() === 'broker_company') return true;
    return this.authService.hasAnyPermission(permissions);
  }
}
